import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { TIER_TINTS } from "@/components/app/UserAvatar";
import { Building2, Sparkles, AlertTriangle, Users } from "lucide-react";

/**
 * Department-grouped conduct summary. Shows each department's total active
 * appreciations / warnings so department-wide recognition (and warnings) are
 * visible at a glance on the dashboard. Anyone authenticated can view.
 */
export default function DepartmentRecognition() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/conduct/department-board").then(({ data }) => {
      if (!cancelled) setRows(data || []);
    }).catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, []);

  if (!rows || rows.length === 0) return null;

  return (
    <section className="bg-white border border-zinc-200 rounded-md p-4 sm:p-5" data-testid="department-recognition">
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="w-4 h-4 text-zinc-500" />
        <h3 className="font-display text-base sm:text-lg font-bold tracking-tight">Department recognition</h3>
        <span className="text-[11px] text-zinc-400">appreciations &amp; warnings rolled up by department</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {rows.map((r) => {
          const tint = TIER_TINTS[r.tier] || TIER_TINTS.none;
          const latest = r.tier === "appreciation" ? r.latest_appreciation : r.latest_warning;
          return (
            <div
              key={r.department}
              className={`rounded-md p-3 bg-white ${tint.row}`}
              data-testid={`dept-recognition-${r.department}`}
              data-conduct-tier={r.tier}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm truncate">{r.department}</span>
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500">
                  <Users className="w-3 h-3" /> {r.member_count}
                </span>
              </div>
              <div className="text-[11px] text-zinc-600 mt-1.5 flex items-baseline gap-2 flex-wrap">
                {r.appreciations > 0 && (
                  <span className="text-emerald-700 inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> <strong>{r.appreciations}</strong> appreciation{r.appreciations === 1 ? "" : "s"}
                  </span>
                )}
                {r.hard > 0 && <span className="text-red-700"><strong>{r.hard}</strong> hard</span>}
                {r.soft > 0 && <span className="text-orange-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> <strong>{r.soft}</strong> soft</span>}
              </div>
              {latest?.reason && (
                <div className="text-[12px] text-zinc-700 mt-2 italic">
                  "{latest.reason}"
                  {latest.issued_by_name && <span className="text-zinc-400 not-italic"> — {latest.issued_by_name}</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
